import type { CSSProperties, HTMLAttributes } from "react";

import "./skeleton.css";

/**
 * `Skeleton` — placeholder visual para componentes que dependen de
 * datos asíncronos (Principio VI). Espeja la estructura del estado
 * cargado mediante `width` / `height` / `radius`, evitando layout
 * shift al sustituirse por el contenido real.
 *
 * Reglas:
 *   - CSS puro con `keyframes` (Principio I).
 *   - Respeta `prefers-reduced-motion: reduce` (animación se detiene).
 *   - `aria-busy="true"` por default; el contenedor padre puede
 *     proveer `role="status"` / `aria-live="polite"` cuando aplique.
 *
 * NOTA (TODO): este es un *stub local* mientras `.packages/galaxia-pro/`
 * no exponga su primitivo `Skeleton` oficial. Cuando lo haga, este
 * archivo DEBE re-exportar desde Galaxia Pro y eliminar el CSS local.
 */

export interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  width?: number | string;
  height?: number | string;
  /** border-radius en px o porcentaje. Default: 4. */
  radius?: number | string;
  /** Si es true, renderiza como bloque (display:block). */
  block?: boolean;
}

export function Skeleton({
  width = "100%",
  height = "1em",
  radius = 4,
  block = false,
  style,
  className,
  ...rest
}: SkeletonProps) {
  const finalStyle: CSSProperties = {
    width,
    height,
    borderRadius: typeof radius === "number" ? `${radius}px` : radius,
    display: block ? "block" : "inline-block",
    ...style,
  };
  return (
    <span
      aria-busy="true"
      aria-hidden="true"
      className={["op-skeleton", className].filter(Boolean).join(" ")}
      style={finalStyle}
      {...rest}
    />
  );
}

/**
 * `SkeletonGroup` — wrapper para regiones con múltiples skeletons.
 * Aplica `role="status"` y `aria-live="polite"` para que las
 * tecnologías de asistencia anuncien el estado pendiente.
 */
export function SkeletonGroup({
  children,
  label = "Cargando…",
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      className={["op-skeleton-group", className].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </div>
  );
}
